import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import Drawer from "@/components/ui/Drawer";

/** The dark side drawer used across the app (bottom sheet on phones and tablets). */
export default function FloatingDrawer({ isOpen, onClose, children, wide }: { isOpen: boolean; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const check = () => setCompact(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (!isOpen || compact) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = previous; window.removeEventListener("keydown", onKey); };
  }, [isOpen, compact, onClose]);

  if (compact) {
    return (
      <Drawer isOpen={isOpen} onClose={onClose} position="bottom" heightPercentage={85} showCloseButton={false}>
        <div className="h-full flex flex-col bg-black text-white">{children}</div>
      </Drawer>
    );
  }

  // Rendered on <body> so the blurred backdrop always covers the whole screen, whatever the page layout.
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[99998]" aria-hidden="true" onClick={onClose} />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            role="dialog"
            aria-modal="true"
            className={`fixed top-4 right-4 bottom-4 ${wide ? "w-[32rem]" : "w-96"} bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">{children}</div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export const darkInput = "w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500";
export const darkLabel = "text-xs font-semibold text-white mb-2 block";
export const darkPrimary = "w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";
export const darkSecondary = "w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50";
export const darkDanger = "w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50";

export function DrawerHeader({ title, subtitle }: { title: string; subtitle?: ReactNode }) {
  return (
    <div className="flex items-center justify-between p-6 border-b border-gray-800">
      <div>
        <h2 className="text-xs font-bold text-white">{title}</h2>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  );
}

export function DetailRow({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-semibold text-white">{label}</p>
        <div className="text-xs text-gray-400 break-words">{children}</div>
      </div>
    </div>
  );
}
